/**
 * Spark Visual Authentication Hook
 *
 * Provides React integration for Spark authentication flow.
 * Uses the Spark Liveness Protocol for secure, lattice-based authentication.
 *
 * Flow:
 * 1. Console creates SparkPayloadEnvelope with login challenge
 * 2. Console emits envelope to /spark/payload/{challenge_id} with TTL
 * 3. Console displays Spark encoding the challenge_id
 * 4. User scans Spark with mobile app
 * 5. App queries lattice for payload, authenticates user
 * 6. App emits SparkAuthResponse to /spark/auth/{session_id}
 * 7. Console receives response and creates session
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useEstreamClient } from './react.js';
import { createConsoleLoginPayload, SPARK_TOPICS, generateChallengeId, } from './spark-liveness.js';
// ============================================================================
// Hook Implementation
// ============================================================================
/**
 * Spark authentication hook using Liveness Protocol
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   const {
 *     state,
 *     startChallenge,
 *     timeRemaining,
 *     isAuthenticated,
 *     challenge,
 *   } = useSpark({
 *     onAuthenticated: (session) => {
 *       router.push('/dashboard');
 *     },
 *   });
 *
 *   return (
 *     <div>
 *       {state.status === 'idle' && (
 *         <button onClick={startChallenge}>
 *           Login with Spark
 *         </button>
 *       )}
 *
 *       {state.status === 'pending' && challenge && (
 *         <SparkRenderer
 *           challengeId={challenge.challengeId}
 *           variant={challenge.variant}
 *         />
 *       )}
 *
 *       {timeRemaining !== null && (
 *         <p>Expires in {timeRemaining}s</p>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSpark(options = {}) {
    const { autoStart = false, onAuthenticated, onError, timeout = 300000, variant = 'kaleidoscope', latticeUrl = typeof window !== 'undefined' ? window.location.origin : '', enablePolling = true, pollingInterval = 2000, retryCount = 3, retryDelay = 1000, serviceWalletId, } = options;
    const client = useEstreamClient();
    const [state, setState] = useState({ status: 'idle' });
    const [timeRemaining, setTimeRemaining] = useState(null);
    const timerRef = useRef(null);
    const subscriptionRef = useRef(null);
    const pollingRef = useRef(null);
    const retryCountRef = useRef(0);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current)
                clearInterval(timerRef.current);
            if (pollingRef.current)
                clearInterval(pollingRef.current);
            if (subscriptionRef.current)
                subscriptionRef.current();
        };
    }, []);
    // Handle successful authentication
    const handleAuthenticated = useCallback((session) => {
        console.log('[Spark] handleAuthenticated called:', session.walletId?.slice(0, 16));
        // Store session FIRST - before callback so getStoredSession() works during navigation
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('estream_session', JSON.stringify(session));
        }
        setState({ status: 'authenticated', session });
        // Cleanup before callback (prevents race conditions)
        if (pollingRef.current)
            clearInterval(pollingRef.current);
        if (subscriptionRef.current)
            subscriptionRef.current();
        // Now call the callback - navigation/side effects happen AFTER session is stored
        onAuthenticated?.(session);
    }, [onAuthenticated]);
    // Poll for verification status (backup)
    const pollVerificationStatus = useCallback(async (sessionId) => {
        if (!enablePolling)
            return;
        try {
            const response = await fetch(`${latticeUrl}/api/auth/spark-status/${sessionId}`);
            if (!response.ok) {
                console.debug('[Spark] Poll response not ok:', response.status);
                return;
            }
            const data = await response.json();
            console.debug('[Spark] Poll response:', { status: data.status, hasSession: !!data.session });
            if (data.status === 'verified' && data.session) {
                const session = data.session;
                console.log('[Spark] Verified! Calling handleAuthenticated');
                handleAuthenticated(session);
            }
        }
        catch (e) {
            console.debug('[Spark] Polling error:', e);
        }
    }, [latticeUrl, enablePolling, handleAuthenticated]);
    // Auto-start if configured
    useEffect(() => {
        if (autoStart && state.status === 'idle') {
            startChallenge();
        }
    }, [autoStart]);
    // Countdown timer
    useEffect(() => {
        if (state.status === 'pending' || state.status === 'verifying') {
            const challenge = state.challenge;
            const updateTimer = () => {
                const remaining = Math.max(0, Math.floor((challenge.expiresAt - Date.now()) / 1000));
                setTimeRemaining(remaining);
                if (remaining <= 0) {
                    setState({ status: 'expired', challenge });
                    if (timerRef.current)
                        clearInterval(timerRef.current);
                }
            };
            updateTimer();
            timerRef.current = setInterval(updateTimer, 1000);
            return () => {
                if (timerRef.current)
                    clearInterval(timerRef.current);
            };
        }
        else {
            setTimeRemaining(null);
        }
    }, [state]);
    const startChallenge = useCallback(async () => {
        const attemptChallenge = async (attempt) => {
            try {
                // Create login payload with Liveness Protocol
                const { payload, envelope } = createConsoleLoginPayload({
                    serviceLattice: `${latticeUrl}/lattice`,
                    serviceWalletId,
                    expiresIn: timeout,
                });
                const challenge = {
                    challengeId: envelope.challenge_id,
                    sessionId: payload.session_id,
                    displayCode: envelope.challenge_id.slice(0, 8).toUpperCase(),
                    expiresAt: payload.expires_at,
                    variant,
                    payload,
                };
                setState({ status: 'pending', challenge });
                retryCountRef.current = attempt;
                // Post challenge to HTTP API for mobile app discovery
                // This is the primary mechanism - lattice is backup
                try {
                    const apiChallenge = {
                        challenge_id: envelope.challenge_id,
                        session_id: payload.session_id,
                        display_code: envelope.challenge_id.slice(0, 8).toUpperCase(),
                        expires_at: payload.expires_at,
                        created_at: Date.now(),
                        payload: envelope.payload,
                    };
                    await fetch(`${latticeUrl}/api/auth/pending-challenges`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(apiChallenge),
                    });
                    console.debug('[Spark] Posted challenge to API:', {
                        challengeId: envelope.challenge_id,
                        sessionId: payload.session_id,
                    });
                }
                catch (apiError) {
                    console.warn('[Spark] Failed to post to API, trying lattice:', apiError);
                    // Fallback: try lattice
                    try {
                        await client.emit(SPARK_TOPICS.PAYLOAD(envelope.challenge_id), envelope);
                        console.debug('[Spark] Emitted payload envelope to lattice');
                    }
                    catch (latticeError) {
                        console.warn('[Spark] Lattice emit also failed:', latticeError);
                    }
                }
                // Subscribe to auth responses on this session
                const authTopic = SPARK_TOPICS.AUTH(payload.session_id);
                const unsubscribe = client.subscribe(authTopic, {
                    onData: (response) => {
                        console.debug('[Spark] Received auth response:', response);
                        // Verify the response matches our challenge
                        if (response.challenge_nonce !== payload.challenge_nonce) {
                            console.warn('[Spark] Challenge nonce mismatch');
                            return;
                        }
                        // Create session from response
                        const session = {
                            token: generateChallengeId(), // Generate session token
                            walletId: response.wallet_id,
                            expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
                        };
                        handleAuthenticated(session);
                    },
                    onError: (error) => {
                        if (attempt < retryCount) {
                            setTimeout(() => attemptChallenge(attempt + 1), retryDelay * (attempt + 1));
                        }
                        else {
                            setState({ status: 'error', error: error.message });
                            onError?.(error.message);
                        }
                    },
                });
                subscriptionRef.current = unsubscribe;
                // Start polling as backup
                if (enablePolling) {
                    pollingRef.current = setInterval(() => {
                        pollVerificationStatus(payload.session_id);
                    }, pollingInterval);
                }
            }
            catch (e) {
                const error = e instanceof Error ? e.message : String(e);
                if (attempt < retryCount) {
                    console.debug(`[Spark] Attempt ${attempt + 1} failed, retrying...`);
                    setTimeout(() => attemptChallenge(attempt + 1), retryDelay * (attempt + 1));
                }
                else {
                    setState({ status: 'error', error });
                    onError?.(error);
                }
            }
        };
        await attemptChallenge(0);
    }, [client, timeout, variant, latticeUrl, serviceWalletId, onAuthenticated, onError, retryCount, retryDelay, enablePolling, pollingInterval, pollVerificationStatus, handleAuthenticated]);
    const cancel = useCallback(() => {
        if (timerRef.current)
            clearInterval(timerRef.current);
        if (pollingRef.current)
            clearInterval(pollingRef.current);
        if (subscriptionRef.current)
            subscriptionRef.current();
        retryCountRef.current = 0;
        setState({ status: 'idle' });
    }, []);
    const signOut = useCallback(() => {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem('estream_session');
        }
        cancel();
    }, [cancel]);
    const isAuthenticated = state.status === 'authenticated';
    const session = state.status === 'authenticated' ? state.session : null;
    const challenge = (state.status === 'pending' || state.status === 'verifying' || state.status === 'expired')
        ? state.challenge
        : null;
    return {
        state,
        startChallenge,
        cancel,
        signOut,
        timeRemaining,
        isAuthenticated,
        session,
        challenge,
    };
}
// ============================================================================
// Utilities
// ============================================================================
/**
 * Restore session from storage
 */
export function getStoredSession() {
    if (typeof sessionStorage === 'undefined')
        return null;
    try {
        const stored = sessionStorage.getItem('estream_session');
        if (!stored)
            return null;
        const session = JSON.parse(stored);
        // Check if expired
        if (session.expiresAt < Date.now()) {
            sessionStorage.removeItem('estream_session');
            return null;
        }
        return session;
    }
    catch {
        return null;
    }
}
export { SPARK_TOPICS, SPARK_TTL, CONSOLE_ACTIONS, GENESIS_ACTIONS, createConsoleLoginPayload, createGenesisKeyHolderChallenge, createGenesisProposalChallenge, parsePayloadEnvelope, generateChallengeId, generateSessionId, generateNonce, EncryptionType, } from './spark-liveness.js';
//# sourceMappingURL=spark.js.map